import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { path } from 'ramda';
import {
  getSubscriptionConfig,
  createSubscriptionPriceGetter,
  triggerNativeIAP,
  doAppVersionRedirect
} from './';
import { isIosApp } from '../../common/ambient/utils';
import './PaymentMethodSelector.less';

function PaymentMethodSelector({ selectedTier, annual, onMethodSelected, user, device, canShowExternalPurchaseLinks = true }) {
  const handlePaymentMethodChoice = (method) => {
    if (method === 'iap') {
      // Trigger IAP flow immediately
      const isTeamsTier = selectedTier === 'teams';
      const subscriptionTerm = annual ? 'yearly' : 'monthly';
      const productName = `awn-plus-${isTeamsTier ? 'for-teams' : 'account'}-${subscriptionTerm}`;
      const userId = path(['info', '_id'], user);

      if (doAppVersionRedirect(productName)) {
        return;
      }

      const success = triggerNativeIAP(productName, userId);
      if (!success) {
        // Fallback: show error or proceed to CC flow
        console.error('Failed to trigger native IAP');
      }
    } else {
      // For CC, proceed to next step
      onMethodSelected(method);
    }
  };

  const getSubscriptionPriceDisplay = createSubscriptionPriceGetter(device);
  const term = annual ? 'yearly' : 'monthly';
  const isTeamsTier = selectedTier === 'teams';

  // Get subscription config and calculate prices
  const subscriptionConfig = getSubscriptionConfig(`awn-plus-${isTeamsTier ? 'for-teams' : 'account'}-${term}`);
  const regularPrice = subscriptionConfig ? subscriptionConfig.price : 0;
  const discountedPrice = (regularPrice * 0.8).toFixed(2);

  // Only use app price for IAP option
  const iapPricing = getSubscriptionPriceDisplay(selectedTier, annual, true);
  const appPrice = iapPricing.price.split('/')[0];

  return (
    <div className='payment-method-selection'>
      <div className='payment-options'>

        {canShowExternalPurchaseLinks && (
          <>
            <div onClick={() => handlePaymentMethodChoice('cc')} className='payment-option cc-option recommended'>
              <span className='badge'>SAVE 20%</span>
              <h4>{subscriptionConfig.displayName} {annual ? 'Annual' : 'Monthly'}</h4>
              <div className='price'>
                <span className='discounted'>${discountedPrice}/{annual ? 'year' : 'month'}<sup>*</sup></span>
              </div>
              <button
                className='btn btn-primary'
              >
                Subscribe with <i className='icon awn' />
              </button>
              <p>Manage subscription through AWN</p>
            </div>
            <div onClick={() => onMethodSelected('gift-code')} className='payment-option iap-option'>
              <h4>AWN+ Gift Code</h4>
              <div className='price'>One year free subscription</div>
              <button className='btn'>
                <i className='icon gift' /> Redeem gift code
              </button>
              <p>Manage subscription through AWN app</p>
            </div>
          </>
        )}

        <div onClick={() => handlePaymentMethodChoice('iap')} className='payment-option iap-option'>
          <h4>{subscriptionConfig.displayName} {annual ? 'Annual' : 'Monthly'}</h4>
          <div className='price'>{appPrice}/{annual ? 'year' : 'month'}</div>
          <button
            className='btn'
          >
            Subscribe with <i className={classNames('icon', isIosApp() ? 'apple' : 'google')}/> {isIosApp() ? 'Apple' : 'Google'}.
          </button>
          <p>Manage subscription through {isIosApp() ? 'Apple' : 'Google'} Store</p>
        </div>
      </div>

      {canShowExternalPurchaseLinks && (
        <p className='fine-print'>
          <sup>*</sup> Secure payment by credit/debit card. 20% off your first billing cycle. Renews at ${regularPrice}/{annual ? 'year' : 'month'} unless cancelled.
        </p>
      )}
    </div>
  );
}

PaymentMethodSelector.propTypes = {
  selectedTier: PropTypes.oneOf(['plus', 'teams']).isRequired,
  annual: PropTypes.bool.isRequired,
  onMethodSelected: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  device: PropTypes.object.isRequired,
  canShowExternalPurchaseLinks: PropTypes.bool
};

export default PaymentMethodSelector;

PaymentMethodSelector.displayName = 'PaymentMethodSelector'
